import React, { useEffect, useMemo, useState } from "react";
import { substituteRunCell, getClassSubjectsByWeek } from "./happyschoolApi";

const DAYS = ["Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"];
const khoiOf = (name) => (String(name || "").match(/^(\d+)/) || [])[1] || "";
const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/đ/gi, "d").toLowerCase();

// GV đã nghỉ, hoặc người trường xác nhận KHÔNG được phân công dạy (dù lịch rảnh 100%) -> không đưa vào danh sách dạy thay.
const NOT_TEACHING_NAMES = new Set(["Hoàng Thị Hoa"]);

// (22/10/2026) GV nghề + người không thuộc khối GV THPT (vd Ban giám hiệu) tách riêng 1 tab, không lẫn với GV THPT.
const VH_SUBJECT_NAMES = new Set(["HĐTN", "HĐTN(SH)", "HĐTN(CC)", "Vật lý", "Ngữ văn", "Toán", "Tiếng anh", "Hóa học", "Sinh học", "Lịch sử", "Công nghệ", "Địa lý", "GDKT&PL", "Pháp luật", "Tin học"]);
// Môn không hiện trong danh sách chọn môn của form (không phải môn văn hoá xếp TKB).
const HIDDEN_SUBJECT_NAMES = new Set(["Lập trình Thiết kế Web", "Pháp luật"]);
const FILTERS = [
  { key: "all", label: "Tất cả GV THPT rảnh" },
  { key: "subject", label: "Cùng môn đã chọn" },
  { key: "khoi", label: "Dạy thay cùng khối" },
  { key: "class", label: "Dạy thay cùng lớp" },
  { key: "present", label: "Trông lớp (có mặt, rảnh)" },
  { key: "khac", label: "GV nghề / khác" },
];
const MODES = [
  { key: "replace", label: "Thay giáo viên (dạy thay)" },
  { key: "supervise", label: "Trông lớp thay (GV có mặt, không dạy môn này)" },
  { key: "clear", label: "Để trống giáo viên" },
  { key: "empty", label: "Để trống tiết (xoá tiết)" },
];

// Dialog admin xử lý 1 ô TKB (mở bằng nhấn đúp ô): thay GV / để trống GV / trông lớp thay,
// kèm lý do, và tìm GV rảnh theo cùng môn / cùng khối / cùng lớp / có mặt trên trường.
// saveOverride (22/10/2026): Case xếp tay dùng chung form này nhưng lưu vào ô xếp tay (không phải bản TKB).
const HappySchoolCellSubstituteDialog = ({ schoolId, runId, weekId, target, grid, classes, subjects, teachers, isBusyAt, periodNotes, onClose, onSaved, saveOverride }) => {
  const { classId, day, period } = target;
  const key = `${classId}|${day}|${period}`;
  const cell = grid[key] || [];
  const subjectId = cell[0];
  const oldTid = cell[1] || null;
  const classById = useMemo(() => Object.fromEntries(classes.map((c) => [c.id, c.name])), [classes]);
  const subjectById = useMemo(() => Object.fromEntries(subjects.map((s) => [s.id, s.name])), [subjects]);
  const teacherById = useMemo(() => Object.fromEntries(teachers.map((t) => [t.id, t])), [teachers]);
  const className = classById[classId] || classId;
  const khoi = khoiOf(className);
  const homeroomTid = (classes.find((c) => c.id === classId) || {}).homeroomTeacherId || null;
  const note = ((periodNotes || {})[classId] || {})[`${day}_${period}`];

  const [mode, setMode] = useState(oldTid ? "replace" : "replace");
  const [filter, setFilter] = useState("subject");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [subjectSel, setSubjectSel] = useState(subjectId); // môn sẽ dạy ở tiết này (đổi sang môn khác = GV môn khác dạy thay)
  const [needMap, setNeedMap] = useState(null); // PCCM hiệu lực tuần này: { subjectId: periodsPerWeek } của lớp
  const [pccmMap, setPccmMap] = useState({}); // subjectId -> teacherId được PCCM cho lớp này (tuần này)

  useEffect(() => {
    if (!schoolId || !weekId) return;
    getClassSubjectsByWeek(schoolId, weekId)
      .then((r) => {
        const cls = ((r && r.effective) || {})[classId] || {};
        setNeedMap(Object.fromEntries(Object.entries(cls).map(([sid, cfg]) => [sid, Number(cfg && cfg.periodsPerWeek) || 0])));
        setPccmMap(Object.fromEntries(Object.entries(cls).filter(([, cfg]) => cfg && cfg.assignedTeacherId).map(([sid, cfg]) => [sid, cfg.assignedTeacherId])));
      })
      .catch(() => setNeedMap({}));
  }, [schoolId, weekId, classId]);

  // Mỗi môn của lớp: đang có bao nhiêu tiết trong bản / cần bao nhiêu (PCCM tuần này) -> biết môn nào thiếu, môn nào đủ.
  const subjectStats = useMemo(() => {
    const have = {};
    Object.entries(grid).forEach(([k, v]) => { if (k.startsWith(`${classId}|`) && Array.isArray(v) && v[0] != null) have[v[0]] = (have[v[0]] || 0) + 1; });
    // Liệt kê TẤT CẢ môn (kể cả môn lớp không có trong PCCM như HĐTN(SH)/HĐTN(CC) do GVCN dạy) để chọn đổi môn được.
    const ids = new Set([...(needMap ? Object.keys(needMap) : []), ...Object.keys(have), ...Object.keys(subjectById)]);
    return [...ids].filter((sid) => subjectById[sid] && !HIDDEN_SUBJECT_NAMES.has(String(subjectById[sid]).normalize("NFC"))).map((sid) => {
      // Sinh hoạt (HĐTN(SH)) không nằm trong PCCM nhưng mỗi lớp luôn cần 1 tiết/tuần.
      const isSH = String(subjectById[sid]).normalize("NFC") === "HĐTN(SH)";
      const need = needMap ? (needMap[sid] || (isSH ? 1 : 0)) : null;
      const h = have[sid] || 0;
      return { sid, name: subjectById[sid], have: h, need, diff: need === null ? 0 : need - h };
    }).sort((a, b) => (b.diff > 0) - (a.diff > 0) || b.diff - a.diff || a.name.localeCompare(b.name, "vi"));
  }, [grid, classId, needMap, subjectById]);

  // Chỉ mục 1 lần từ grid: GV nào dạy lúc nào / lớp nào / khối nào / môn nào.
  const info = useMemo(() => {
    const teachingAt = {}; // tid -> Set("d|p")
    const daysPresent = {}; // tid -> {day: count}
    const classesOf = {}; // tid -> Set(classId)
    const khoisOf = {}; // tid -> Set(khoi)
    const subjectsOf = {}; // tid -> Set(subjectId)
    Object.entries(grid).forEach(([k, v]) => {
      if (!Array.isArray(v) || !v[1] || typeof v[1] !== "string") return;
      const [cid, d, p] = k.split("|");
      const t = v[1];
      (teachingAt[t] = teachingAt[t] || new Set()).add(`${d}|${p}`);
      daysPresent[t] = daysPresent[t] || {};
      daysPresent[t][d] = (daysPresent[t][d] || 0) + 1;
      (classesOf[t] = classesOf[t] || new Set()).add(cid);
      (khoisOf[t] = khoisOf[t] || new Set()).add(khoiOf(classById[cid]));
      (subjectsOf[t] = subjectsOf[t] || new Set()).add(v[0]);
    });
    return { teachingAt, daysPresent, classesOf, khoisOf, subjectsOf };
  }, [grid, classById]);

  const candidates = useMemo(() => {
    return teachers
      .filter((t) => t.id !== oldTid)
      .filter((t) => t.status !== "Đã nghỉ" && !NOT_TEACHING_NAMES.has(String(t.name || "").normalize("NFC").trim()))
      .filter((t) => !isBusyAt(t.id, day, period) && !(info.teachingAt[t.id] && info.teachingAt[t.id].has(`${day}|${period}`)))
      .map((t) => {
        // HĐTN(SH)/HĐTN(CC)/HĐTN do GVCN của lớp phụ trách -> GVCN lớp coi như "cùng môn".
        const isHdtn = /^HĐTN/.test(String(subjectById[subjectSel] || "").normalize("NFC"));
        const isGvcn = !!homeroomTid && t.id === homeroomTid;
        const sameSubject = (t.subjectIds || []).includes(subjectSel) || !!(info.subjectsOf[t.id] && info.subjectsOf[t.id].has(subjectSel)) || (isHdtn && isGvcn);
        const sameKhoi = !!(info.khoisOf[t.id] && info.khoisOf[t.id].has(khoi));
        const sameClass = !!(info.classesOf[t.id] && info.classesOf[t.id].has(classId));
        const presentCount = (info.daysPresent[t.id] || {})[String(day)] || 0;
        // GV được PCCM môn đang chọn cho chính lớp này (tuần này) -> gắn nhãn "PCCM", xếp đầu danh sách.
        const isPccm = !!subjectSel && (pccmMap[subjectSel] === t.id || (!pccmMap[subjectSel] && isHdtn && isGvcn));
        // "Khác": không thuộc khối GV THPT (vd Ban giám hiệu) hoặc GV nghề (chỉ khai môn nghề, không có môn văn hoá nào).
        const subjNames = (t.subjectIds || []).map((sid) => String(subjectById[sid] || "").normalize("NFC")).filter(Boolean);
        const isNghe = subjNames.length > 0 && !subjNames.some((n) => VH_SUBJECT_NAMES.has(n));
        const isOther = (t.department && String(t.department).normalize("NFC") !== "Giáo viên THPT") || isNghe;
        return { t, sameSubject, sameKhoi, sameClass, presentCount, isPccm, isOther };
      });
  }, [teachers, oldTid, isBusyAt, day, period, info, subjectSel, khoi, classId, homeroomTid, subjectById, pccmMap]);

  const shown = useMemo(() => {
    const qn = norm(q);
    return candidates
      .filter((c) => {
        if (filter === "khac") return c.isOther;
        if (c.isPccm) return true; // GV được PCCM luôn hiện, bộ lọc nào cũng thấy
        if (c.isOther) return false; // GV nghề / khác chỉ hiện ở tab riêng
        if (filter === "subject") return c.sameSubject;
        if (filter === "khoi") return c.sameKhoi;
        if (filter === "class") return c.sameClass;
        if (filter === "present") return c.presentCount > 0;
        return true;
      })
      .filter((c) => !qn || norm(c.t.name).includes(qn))
      .sort((a, b) => (b.isPccm - a.isPccm) || (b.sameSubject - a.sameSubject) || (b.sameClass - a.sameClass) || (b.sameKhoi - a.sameKhoi) || (b.presentCount - a.presentCount) || a.t.name.localeCompare(b.t.name, "vi"));
  }, [candidates, filter, q]);

  const counts = useMemo(() => ({
    all: candidates.filter((c) => !c.isOther || c.isPccm).length,
    subject: candidates.filter((c) => c.isPccm || (!c.isOther && c.sameSubject)).length,
    khoi: candidates.filter((c) => c.isPccm || (!c.isOther && c.sameKhoi)).length,
    class: candidates.filter((c) => c.isPccm || (!c.isOther && c.sameClass)).length,
    present: candidates.filter((c) => c.isPccm || (!c.isOther && c.presentCount > 0)).length,
    khac: candidates.filter((c) => c.isOther).length,
  }), [candidates]);

  // Chọn GV dạy thay: nếu GV này KHÔNG dạy môn đang chọn (khai báo môn dạy của GV) thì TỰ ĐỔI môn của tiết
  // sang môn của GV đó (ưu tiên môn lớp đang thiếu tiết) — vd thay cô Địa lý bằng cô Tiếng Anh thì tiết thành Tiếng Anh.
  // Vẫn bấm chọn lại môn khác được sau đó.
  const pick = (t) => {
    setPicked(t.id);
    if (mode !== "replace") return;
    const own = (t.subjectIds || []).filter((sid) => subjectById[sid]);
    if (own.length === 0 || own.includes(subjectSel)) return;
    // Tiết HĐTN (Sinh hoạt/Chào cờ) do GVCN dạy, không gắn môn chuyên -> giữ nguyên, không tự đổi sang môn của GV (vd Lập trình Web).
    if (/^HĐTN/.test(String(subjectById[subjectSel] || "").normalize("NFC"))) return;
    const lackOf = (sid) => { const st = subjectStats.find((x) => x.sid === sid); return st && st.need !== null ? st.diff : 0; };
    setSubjectSel(own.slice().sort((a, b) => lackOf(b) - lackOf(a))[0]);
  };

  const save = async () => {
    setErr("");
    const noTeacher = mode === "clear" || mode === "empty";
    if (!noTeacher && !picked) { setErr("Chọn 1 giáo viên trong danh sách bên dưới."); return; }
    if (mode === "empty" && !window.confirm(`Xoá hẳn tiết ${period + 1} ${DAYS[day]} của lớp ${className}? Lớp sẽ trống tiết này.`)) return;
    setSaving(true);
    try {
      const payload = { classId, day, period, mode, newTeacherId: noTeacher ? undefined : picked, newSubjectId: mode === "replace" && subjectSel !== subjectId ? subjectSel : undefined, reason };
      const r = saveOverride ? await saveOverride(payload) : await substituteRunCell(schoolId, runId, payload);
      if (r && r.pending) window.alert(r.message || "Đã gửi yêu cầu sửa — chờ duyệt.");
      onSaved && onSaved(r);
    } catch (e) {
      setErr(e.message || "Lưu thất bại.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Xử lý tiết: {className} — {DAYS[day]} tiết {period + 1}</h3>
          <button type="button" onClick={onClose} style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>
        <p style={{ margin: "6px 0 10px", color: "#374151", fontSize: 14 }}>
          Môn <b>{subjectById[subjectId] || subjectId}</b> · GV hiện tại: <b>{oldTid ? (teacherById[oldTid] ? teacherById[oldTid].name : oldTid) : "(đang trống)"}</b>
          {note && <span style={{ display: "block", color: "#92400e", fontSize: 12 }}>📝 Ghi chú hiện có: {note.note}</span>}
        </p>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
          {MODES.map((m) => (
            <label key={m.key} style={{ fontSize: 14, cursor: "pointer" }}>
              <input type="radio" name="sub-mode" checked={mode === m.key} onChange={() => { setMode(m.key); if (m.key === "supervise") setFilter("present"); else if (m.key === "replace") setFilter("subject"); }} /> {m.label}
            </label>
          ))}
        </div>

        <label style={{ display: "block", fontSize: 14, marginBottom: 8 }}>Lý do / ghi chú
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="VD: cô X nghỉ ốm, thầy Y đi công tác..."
            style={{ width: "100%", boxSizing: "border-box", marginTop: 4, padding: 6, border: "1px solid #d1d5db", borderRadius: 6 }} />
        </label>

        {mode === "replace" && (
          <div style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#6b7280", margin: "4px 0" }}>
              Môn học của tiết này — chọn môn khác = GV môn đó dạy thay (số tiết có / cần của {className} tuần này)
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {subjectStats.map((st) => {
                const lack = st.need !== null && st.diff > 0;
                const over = st.need !== null && st.diff < 0;
                const on = subjectSel === st.sid;
                return (
                  <button key={st.sid} type="button" onClick={() => { setSubjectSel(st.sid); setPicked(""); }}
                    title={st.need === null ? "Đang tải số tiết cần..." : lack ? `Thiếu ${st.diff} tiết` : over ? `Dư ${-st.diff} tiết so với cấu hình` : "Đã đủ tiết"}
                    style={{ padding: "4px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
                      border: `1px solid ${on ? "#0ea5a3" : lack ? "#f59e0b" : "#d1d5db"}`,
                      background: on ? "#0ea5a3" : lack ? "#fffbeb" : "#fff",
                      color: on ? "#fff" : lack ? "#b45309" : "#374151" }}>
                    {st.name}{st.sid === subjectId ? " (hiện tại)" : ""}: {st.have}/{st.need === null ? "…" : st.need}
                    {lack ? ` ⚠ thiếu ${st.diff}` : over ? ` ↑ dư ${-st.diff}` : st.need !== null ? " ✓ đủ" : ""}
                  </button>
                );
              })}
            </div>
            {subjectSel !== subjectId && (
              <div style={{ fontSize: 12, color: "#92400e", marginTop: 4 }}>
                Tiết này sẽ đổi từ <b>{subjectById[subjectId]}</b> sang <b>{subjectById[subjectSel]}</b>; {subjectById[subjectId]} của lớp sẽ giảm 1 tiết. (Bấm môn khác ở trên nếu muốn giữ môn cũ.)
              </div>
            )}
            {(subjectSel || homeroomTid) && (() => {
              // PCCM của môn đang chọn cho lớp này (tuần này; môn HĐTN = GVCN lớp) + GVCN lớp — luôn hiện kèm tình trạng
              // đúng giờ này (kể cả khi GV đó không rảnh nên không có trong danh sách bên dưới), nút chọn nhanh nếu rảnh.
              const isHdtnSel = /^HĐTN/.test(String(subjectById[subjectSel] || "").normalize("NFC"));
              const statusOf = (tid) => {
                const t = teacherById[tid];
                const k = Object.keys(grid).find((kk) => { const v = grid[kk]; const [, d, p] = kk.split("|"); return kk !== key && Array.isArray(v) && v[1] === tid && Number(d) === day && Number(p) === period; });
                if (tid === oldTid) return ["là GV đang dạy tiết này", false];
                if (t && t.status === "Đã nghỉ") return ["đã nghỉ việc", false];
                if (isBusyAt(tid, day, period)) return ["khai bận giờ này", false];
                if (k) return [`đang dạy ${classById[k.split("|")[0]] || "lớp khác"} giờ này`, false];
                return ["rảnh giờ này", true];
              };
              const pccmTid = subjectSel ? (pccmMap[subjectSel] || (isHdtnSel ? homeroomTid : null)) : null;
              const rows = [];
              if (subjectSel) rows.push({ label: <>📋 PCCM <b>{subjectById[subjectSel]}</b> lớp {className}{isHdtnSel && !pccmMap[subjectSel] ? " (GVCN)" : ""}</>, tid: pccmTid });
              if (homeroomTid && homeroomTid !== pccmTid) rows.push({ label: <>👩‍🏫 GVCN lớp {className}</>, tid: homeroomTid });
              return (
                <div style={{ marginTop: 8, padding: "8px 10px", borderRadius: 8, fontSize: 13, background: "#f0f9ff", border: "1px solid #bae6fd", display: "grid", gap: 4 }}>
                  {rows.map(({ label, tid }, i) => {
                    const t = tid ? teacherById[tid] : null;
                    const [status, free] = tid ? statusOf(tid) : ["", false];
                    return (
                      <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span>{label}: {tid ? <b>{t ? t.name : tid}</b> : <i>chưa phân công</i>}</span>
                        {tid && <span style={{ color: free ? "#047857" : "#b45309" }}>— {status}</span>}
                        {free && picked !== tid && <button type="button" onClick={() => setPicked(tid)} style={{ padding: "2px 10px", border: "1px solid #0ea5a3", borderRadius: 6, background: "#fff", color: "#0f766e", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>Chọn GV này</button>}
                    {free && picked === tid && <span style={{ color: "#047857", fontWeight: 600 }}>✓ đã chọn</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {mode === "empty" && <p style={{ fontSize: 13, color: "#b91c1c", margin: "4px 0 8px" }}>Xoá hẳn tiết này khỏi TKB (lớp nghỉ tiết này). Ghi chú của ô cũng bị xoá. Ô xếp tay (nếu có) không bị đụng tới.</p>}
        {mode !== "clear" && mode !== "empty" && (
          <>
            <div style={{ fontWeight: 700, fontSize: 13, color: "#6b7280", margin: "4px 0" }}>Tìm giáo viên rảnh đúng giờ này (không khai bận, không dạy lớp khác)</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
              {FILTERS.map((f) => (
                <button key={f.key} type="button" onClick={() => setFilter(f.key)} style={filter === f.key ? chipOn : chip}>{f.label} ({counts[f.key]})</button>
              ))}
            </div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm theo tên giáo viên..." style={{ width: "100%", boxSizing: "border-box", padding: 6, border: "1px solid #d1d5db", borderRadius: 6, marginBottom: 6 }} />
            <div style={{ maxHeight: 240, overflowY: "auto", border: "1px solid #e5e7eb", borderRadius: 6 }}>
              {shown.length === 0 ? (
                <p style={{ color: "#6b7280", fontSize: 13, padding: 10, margin: 0 }}>Không có giáo viên nào phù hợp bộ lọc này.</p>
              ) : shown.map((c) => (
                <label key={c.t.id} style={{ display: "flex", gap: 8, alignItems: "center", padding: "6px 10px", borderBottom: "1px solid #f3f4f6", cursor: "pointer", background: picked === c.t.id ? "#ecfdf5" : "#fff" }}>
                  <input type="radio" name="sub-teacher" checked={picked === c.t.id} onChange={() => pick(c.t)} />
                  <span style={{ fontWeight: 600 }}>{c.t.name}</span>
                  <span style={{ color: "#6b7280", fontSize: 12 }}>{c.t.employmentType || ""}</span>
                  <span style={{ marginLeft: "auto", display: "flex", gap: 4, flexWrap: "wrap" }}>
                    {c.isPccm && <span style={tag("#0369a1")}>📋 PCCM {subjectById[subjectSel]}</span>}
                    {homeroomTid === c.t.id && <span style={tag("#be185d")}>GVCN</span>}
                    {c.sameSubject && <span style={tag("#0f766e")}>cùng môn</span>}
                    {c.sameClass && <span style={tag("#1d4ed8")}>cùng lớp</span>}
                    {c.sameKhoi && <span style={tag("#7c3aed")}>cùng khối</span>}
                    {c.presentCount > 0 && <span style={tag("#b45309")}>có mặt {c.presentCount} tiết</span>}
                  </span>
                </label>
              ))}
            </div>
          </>
        )}

        {err && <p style={{ color: "#dc2626", margin: "8px 0 0" }}>{err}</p>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" onClick={onClose} disabled={saving} style={btnGhost}>Hủy</button>
          <button type="button" onClick={save} disabled={saving} style={btnPrimary}>{saving ? "Đang lưu..." : mode === "empty" ? "Xoá tiết (để trống)" : mode === "clear" ? "Cập nhật về trống GV" : mode === "supervise" ? "Xác nhận trông lớp" : "Xác nhận dạy thay"}</button>
        </div>
      </div>
    </div>
  );
};

const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 };
const box = { background: "#fff", borderRadius: 12, padding: 18, width: 640, maxWidth: "94vw", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 20px 40px rgba(0,0,0,.2)" };
const chip = { padding: "4px 10px", border: "1px solid #d1d5db", borderRadius: 999, background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#374151" };
const chipOn = { ...chip, background: "#0ea5a3", borderColor: "#0ea5a3", color: "#fff" };
const tag = (color) => ({ fontSize: 11, padding: "1px 6px", borderRadius: 999, border: `1px solid ${color}`, color });
const btnGhost = { padding: "8px 14px", border: "1px solid #d1d5db", background: "#fff", borderRadius: 6, cursor: "pointer", fontWeight: 600 };
const btnPrimary = { padding: "8px 16px", border: "none", background: "#0ea5a3", color: "#fff", borderRadius: 6, cursor: "pointer", fontWeight: 700 };

export default HappySchoolCellSubstituteDialog;
